import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CalendarCheck, Link2, Unlink, ShieldCheck, Info, Users2, KeyRound } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { calendarStatus, calendarConnect, calendarAdminConsent, calendarDisconnect, calendarReps } from '../lib/marketingApi';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

export default function CalendarConnect() {
  const { user } = useAdminAuth();
  const isAdmin = user?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [conn, setConn] = useState({ connected: false });
  const [reps, setReps] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([calendarStatus(), calendarReps().catch(() => ({ reps: [] }))]);
      setConfigured(!!s.configured);
      setConn(s.connection || { connected: false });
      setReps(r.reps || []);
    } catch (e) { toast.error('Failed to load calendar status'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ms = params.get('ms');
    if (ms === 'connected') { toast.success('Microsoft 365 calendar connected'); setParams({}, { replace: true }); }
    else if (ms === 'admin_consent_ok') { toast.success('Tenant admin consent granted — reps can now connect without approval'); setParams({}, { replace: true }); }
    else if (ms === 'error') { toast.error(`Could not connect (${params.get('reason') || 'error'})`); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try {
      const d = await calendarConnect();
      window.location.href = d.auth_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Microsoft sign-in'); setBusy(false); }
  };
  const grantAdminConsent = async () => {
    setBusy(true);
    try {
      const d = await calendarAdminConsent();
      window.location.href = d.consent_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start admin consent'); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await calendarDisconnect(); toast.success('Calendar disconnected'); await load(); }
    catch (e) { toast.error('Disconnect failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="calendar-connect-page">
      <AdminHeader active="my-calendar" />
      <div className="mx-auto max-w-3xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><CalendarCheck className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">My Calendar</h1>
            <p className="text-sm text-slate-500">Connect your Microsoft 365 mailbox so you can book Teams calls and send enquiry replies from your own account.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0035A8]" /></div>
        ) : !configured ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="calendar-not-configured">
            <div className="mb-1 flex items-center gap-2 font-semibold"><Info className="h-4 w-4" /> Microsoft Graph is not configured</div>
            Ask an administrator to set the Azure credentials and <code>MS_CALENDAR_REDIRECT_URI</code> in the backend environment.
          </div>
        ) : (
          <div className="space-y-4">
            {isAdmin && (
              <div className="rounded-lg border border-[#0035A8]/20 bg-[#0035A8]/5 p-5" data-testid="calendar-admin-consent-card">
                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="max-w-xl">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><KeyRound className="h-4 w-4 text-[#0035A8]" /> One-time tenant setup (admin)</div>
                    <p className="mt-1 text-xs text-slate-600">If reps see a <span className="font-semibold">"Need admin approval / access denied"</span> screen when connecting, grant tenant-wide admin consent once here. After that, every rep can connect with no approval step.</p>
                  </div>
                  <button onClick={grantAdminConsent} disabled={busy} data-testid="calendar-admin-consent-btn"
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-[#0035A8] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Grant admin consent
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="calendar-connection-card">
              {conn.connected ? (
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><ShieldCheck className="h-5 w-5" /></span>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{conn.ms_name || conn.ms_email}</div>
                      <div className="text-xs text-slate-500">{conn.ms_email} · connected {fmt(conn.connected_at)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Connected</span>
                    <button onClick={disconnect} disabled={busy} data-testid="calendar-disconnect-btn"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />} Disconnect
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Not connected yet</div>
                    <p className="mt-0.5 text-xs text-slate-500">Sign in with Microsoft to let the CRM read your free/busy and create Teams meetings on your behalf.</p>
                  </div>
                  <button onClick={connect} disabled={busy} data-testid="calendar-connect-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Connect Microsoft 365
                  </button>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="calendar-reps-card">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Users2 className="h-4 w-4 text-[#0035A8]" /> Connected reps</div>
              {reps.length === 0 ? (
                <p className="text-sm text-slate-400">No reps have connected a calendar yet.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {reps.map((r) => (
                    <div key={r.crm_user_id} className="flex items-center justify-between py-2.5 text-sm">
                      <span className="font-medium text-slate-800">{r.name}{r.is_me && <span className="ml-2 rounded bg-[#0035A8]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#0035A8]">You</span>}</span>
                      <span className="text-slate-500">{r.ms_email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-start gap-2 rounded-md bg-slate-50 p-3.5 text-xs text-slate-500">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Once connected, open any enquiry to <span className="font-semibold">Book a Teams call</span> or send an AI-drafted <span className="font-semibold">Reply</span> straight from your own mailbox. Already connected before this update? Click <span className="font-semibold">Disconnect</span> then reconnect once to grant the new send-email permission.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
